import {
  CartLinesUpdateEvent,
  StandardEvents
} from '@shopify/events';


/* =========================================================
   RIDESELECT - ARVELO PACE FAMILY PACK
========================================================= */

const ROOT = window.Shopify?.routes?.root || '/';

const PACE_HANDLE = 'arvelo-pace';

const FAMILY_PACK_PRODUCTS = [
  'arvelo-monkey-bars',
  'arvelo-front-basket',
  'arvelo-l-kussen'
];

const FAMILY_PACK_PROPERTY = '_rideselect_family_pack';

let isSyncing = false;
let syncQueued = false;

const productCache = new Map();


/* =========================================================
   HELPERS
========================================================= */

function getCartSections() {
  const ids = [];

  document
    .querySelectorAll('cart-items-component[data-section-id]')
    .forEach((element) => {
      const id = element.dataset.sectionId;

      if (id && !ids.includes(id)) {
        ids.push(id);
      }
    });

  return ids;
}


async function getCart() {
  const response = await fetch(`${ROOT}cart.js`, {
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json'
    }
  });

  if (!response.ok) {
    throw new Error('Kon winkelwagen niet ophalen.');
  }

  return response.json();
}


async function getProduct(handle) {
  if (productCache.has(handle)) {
    return productCache.get(handle);
  }

  const response = await fetch(
    `${ROOT}products/${encodeURIComponent(handle)}.js`,
    {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json'
      }
    }
  );

  if (!response.ok) {
    throw new Error(`Kon product ${handle} niet ophalen.`);
  }

  const product = await response.json();

  productCache.set(handle, product);

  return product;
}


async function getAvailableVariantId(handle) {
  const product = await getProduct(handle);

  const variant =
    product.variants.find((variant) => variant.available) ||
    product.variants[0];

  if (!variant) {
    throw new Error(`Geen variant gevonden voor ${handle}.`);
  }

  return variant.id;
}


function isFamilyPackItem(item) {
  return (
    item.properties &&
    item.properties[FAMILY_PACK_PROPERTY] === 'true'
  );
}


/* =========================================================
   HORIZON CART EVENT
========================================================= */

function createCartEvent(action, lines) {
  const deferred = CartLinesUpdateEvent.createPromise();

  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action,
      context: 'cart',
      lines,
      promise: deferred.promise
    })
  );

  return deferred;
}


async function resolveCartEvent(deferred, response) {
  const cart = await getCart();

  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),

    detail: {
      items: cart.items,
      itemCount: cart.item_count,
      sections: response?.sections || {},
      source: 'rideselect-family-pack',
      didError: false
    }
  });

  return cart;
}


/* =========================================================
   ADD FAMILY PACK
========================================================= */

async function addFamilyPack(handles) {
  if (!handles.length) return;

  const variantIds = await Promise.all(
    handles.map((handle) => getAvailableVariantId(handle))
  );

  const items = variantIds.map((id) => ({
    id,
    quantity: 1,

    properties: {
      [FAMILY_PACK_PROPERTY]: 'true'
    }
  }));

  const sections = getCartSections();

  const deferred = createCartEvent(
    'add',
    items.map((item) => ({
      merchandiseId: String(item.id),
      quantity: 1
    }))
  );

  const body = {
    items
  };

  if (sections.length) {
    body.sections = sections.join(',');
    body.sections_url = window.location.pathname;
  }

  try {
    const response = await fetch(`${ROOT}cart/add.js`, {
      method: 'POST',

      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },

      credentials: 'same-origin',

      body: JSON.stringify(body)
    });

    const data = await response.json();

    if (!response.ok || data.status) {
      throw new Error(
        data.description ||
        data.message ||
        'Family Pack kon niet worden toegevoegd.'
      );
    }

    await resolveCartEvent(deferred, data);

  } catch (error) {
    deferred.reject(error);

    console.error(
      '[RideSelect Family Pack] Toevoegen mislukt:',
      error
    );
  }
}


/* =========================================================
   REMOVE / CORRECT FAMILY PACK
========================================================= */

async function updateFamilyPack(updates, action = 'update') {
  const keys = Object.keys(updates);

  if (!keys.length) return;

  const sections = getCartSections();

  const deferred = createCartEvent(
    action,
    keys.map((key) => ({
      id: key,
      quantity: updates[key]
    }))
  );

  const body = {
    updates
  };

  if (sections.length) {
    body.sections = sections.join(',');
    body.sections_url = window.location.pathname;
  }

  try {
    const response = await fetch(`${ROOT}cart/update.js`, {
      method: 'POST',

      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },

      credentials: 'same-origin',

      body: JSON.stringify(body)
    });

    const data = await response.json();

    if (!response.ok || data.status) {
      throw new Error(
        data.description ||
        data.message ||
        'Family Pack kon niet worden bijgewerkt.'
      );
    }

    await resolveCartEvent(deferred, data);

  } catch (error) {
    deferred.reject(error);

    console.error(
      '[RideSelect Family Pack] Bijwerken mislukt:',
      error
    );
  }
}


/* =========================================================
   SYNC LOGIC
========================================================= */

async function syncFamilyPack() {
  if (isSyncing) {
    syncQueued = true;
    return;
  }

  isSyncing = true;

  try {
    const cart = await getCart();

    const hasPace = cart.items.some(
      (item) => item.handle === PACE_HANDLE
    );


    /* -----------------------------------------------------
       GEEN PACE = AUTOMATISCHE CADEAUS VERWIJDEREN
    ----------------------------------------------------- */

    if (!hasPace) {
      const updates = {};

      cart.items.forEach((item) => {
        if (isFamilyPackItem(item)) {
          updates[item.key] = 0;
        }
      });

      if (Object.keys(updates).length) {
        await updateFamilyPack(updates, 'remove');
      }

      return;
    }


    /* -----------------------------------------------------
       PACE AANWEZIG = CONTROLEREN WELKE CADEAUS ONTBREKEN
    ----------------------------------------------------- */

    const missingHandles = FAMILY_PACK_PRODUCTS.filter((handle) => {
      return !cart.items.some(
        (item) => item.handle === handle
      );
    });

    if (missingHandles.length) {
      await addFamilyPack(missingHandles);
      return;
    }


    /* -----------------------------------------------------
       AUTOMATISCH CADEAU ALTIJD MAX. 1 STUK
    ----------------------------------------------------- */

    const quantityUpdates = {};

    cart.items.forEach((item) => {
      if (
        isFamilyPackItem(item) &&
        item.quantity !== 1
      ) {
        quantityUpdates[item.key] = 1;
      }
    });

    if (Object.keys(quantityUpdates).length) {
      await updateFamilyPack(quantityUpdates);
    }

  } catch (error) {
    console.error(
      '[RideSelect Family Pack] Synchronisatie mislukt:',
      error
    );

  } finally {
    isSyncing = false;

    if (syncQueued) {
      syncQueued = false;

      setTimeout(() => {
        syncFamilyPack();
      }, 100);
    }
  }
}


/* =========================================================
   RUN AFTER CART CHANGES
========================================================= */

function queueFamilyPackSync() {
  clearTimeout(queueFamilyPackSync.timer);

  queueFamilyPackSync.timer = setTimeout(() => {
    syncFamilyPack();
  }, 150);
}


document.addEventListener(
  StandardEvents.cartLinesUpdate,
  (event) => {

    event.promise
      ?.then(({ detail }) => {

        if (
          detail?.source === 'rideselect-family-pack'
        ) {
          return;
        }

        queueFamilyPackSync();

      })
      .catch(() => {});
  }
);


/* =========================================================
   INITIAL CHECK
========================================================= */

if (document.readyState === 'loading') {

  document.addEventListener(
    'DOMContentLoaded',
    () => {
      queueFamilyPackSync();
    },
    { once: true }
  );

} else {

  queueFamilyPackSync();

}